/**
 * EIGOO TechOpssa — Behavioral Biometrics Capture (frontend)
 * Tracks keystroke cadence, mouse entropy, device fingerprint, session timing.
 * Telemetry submitted with every login attempt.
 */

let _mountAt = null;
let _mouseTotalPx = 0;
let _lastMouse = null;
let _kbdTimes = [];
let _attached = false;

const onMouseMove = (e) => {
  if (_lastMouse) {
    const dx = e.clientX - _lastMouse.x;
    const dy = e.clientY - _lastMouse.y;
    _mouseTotalPx += Math.sqrt(dx * dx + dy * dy);
  }
  _lastMouse = { x: e.clientX, y: e.clientY };
};

export const startCapture = () => {
  _mountAt = performance.now();
  _mouseTotalPx = 0;
  _lastMouse = null;
  _kbdTimes = [];
  if (!_attached) {
    window.addEventListener("mousemove", onMouseMove, { passive: true });
    _attached = true;
  }
};

export const stopCapture = () => {
  if (_attached) {
    window.removeEventListener("mousemove", onMouseMove);
    _attached = false;
  }
};

export const recordKeystroke = () => {
  _kbdTimes.push(performance.now());
};

const _intervals = () => {
  if (_kbdTimes.length < 2) return [];
  const out = [];
  for (let i = 1; i < _kbdTimes.length; i++) {
    out.push(Math.round(_kbdTimes[i] - _kbdTimes[i - 1]));
  }
  return out;
};

// Stable device fingerprint — canvas + UA + screen + tz + lang + plugins-len
const computeFingerprint = () => {
  try {
    const canvas = document.createElement("canvas");
    canvas.width = 200;
    canvas.height = 40;
    const ctx = canvas.getContext("2d");
    ctx.textBaseline = "top";
    ctx.font = "14px 'Cinzel', serif";
    ctx.fillStyle = "#c8a24b";
    ctx.fillRect(0, 0, 200, 40);
    ctx.fillStyle = "#0c0c11";
    ctx.fillText("EIGOO TechOpssa · Sovereign", 4, 8);
    ctx.strokeStyle = "rgba(220,185,95,0.5)";
    ctx.beginPath();
    ctx.arc(180, 20, 14, 0, Math.PI * 2);
    ctx.stroke();
    const dataUrl = canvas.toDataURL();

    const seed = [
      navigator.userAgent || "",
      navigator.language || "",
      `${window.screen.width}x${window.screen.height}x${window.devicePixelRatio || 1}`,
      Intl.DateTimeFormat().resolvedOptions().timeZone || "",
      (navigator.plugins?.length || 0).toString(),
      dataUrl,
    ].join("||");

    // simple djb2 hash
    let h = 5381;
    for (let i = 0; i < seed.length; i++) h = ((h * 33) ^ seed.charCodeAt(i)) >>> 0;
    return ("00000000" + h.toString(16)).slice(-8) + (seed.length.toString(16)).padStart(4, "0");
  } catch {
    return "unfingerprintable";
  }
};

// Shannon-ish entropy estimate of mouse path bucketing
const _entropy = () => {
  if (_mouseTotalPx === 0) return 0;
  const session = (performance.now() - (_mountAt || performance.now())) / 1000;
  if (session <= 0) return 0;
  const ratio = _mouseTotalPx / session;
  return Math.min(8, Math.log2(1 + ratio));
};

export const collectTelemetry = () => {
  const session_ms = _mountAt ? Math.round(performance.now() - _mountAt) : 0;
  return {
    device_fp: computeFingerprint(),
    ua: navigator.userAgent || "",
    tz: Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC",
    screen: `${window.screen.width}x${window.screen.height}`,
    lang: navigator.language || "",
    kbd_intervals_ms: _intervals(),
    mouse_total_px: Math.round(_mouseTotalPx),
    mouse_path_entropy: Number(_entropy().toFixed(2)),
    session_ms,
  };
};

export const resetCapture = () => {
  _mountAt = performance.now();
  _mouseTotalPx = 0;
  _lastMouse = null;
  _kbdTimes = [];
};
